import { ReadOnlyBanner } from './ReadOnlyBanner';
import { PaymentsDialog } from '../payments/PaymentsDialog';
import { NewPasswordRequired } from './NewPasswordRequired';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DockviewReact,
  type DockviewApi,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
} from 'dockview-react';
import { ConfirmDialog, shellOf, usePrefsSync, usePrefsValue, useSession } from '@adiox/ui';
import { prefsStore } from '@adiox/ui';
import { AlertsPanel } from '../alerts/AlertsPanel';
import { useAlertWatcher } from '../alerts/useAlertWatcher';
import { ChallengePanel } from '../challenge/ChallengePanel';
import { ChartPanel } from '../chart/ChartPanel';
import { HistoryPanel } from '../history/HistoryPanel';
import { NotificationFeed } from '../notifications/NotificationFeed';
import { NotificationsPanel } from '../notifications/NotificationsPanel';
import { notifications, persistNotifications } from '../notifications/notificationStore';
import { useNotificationEffects } from '../notifications/useNotificationEffects';
import { Toasts } from '../notifications/Toasts';
import { OrderTicket } from '../trading/OrderTicket';
import { SettingsDialog } from './SettingsDialog';
import { ShortcutsDialog } from './ShortcutsDialog';
import { WelcomeGuide } from './WelcomeGuide';
import { useGuideDue } from './guide';
import { useDocumentTitle } from './useDocumentTitle';
import { LayoutContext, useIsNarrow } from './layout';
import { MobileWorkspace } from '../mobile/MobileWorkspace';
import { TradePanel } from '../trading/TradePanel';
import { MarketWatch } from '../market/MarketWatch';
import { DataWindow } from '../chart/DataWindow';
import { TickChart } from '../chart/TickChart';
import { ProfilesDialog } from './ProfilesDialog';
import { MailboxPanel } from '../mailbox/MailboxPanel';
import { useMailAnnouncer } from '../mailbox/useMailbox';
import { StreamProvider } from '../stream/StreamProvider';
import { Wordmark } from '@adiox/ui';
import { DockTab } from './DockTab';
import { TopBar, type PanelEntry } from './TopBar';
import { useWorkspace } from '../workspace/workspaceStore';
import { useChallenge } from '../challenge/useChallenge';
import { normalizeProfiles } from './profiles';
import { MenuBar, type ClassicActions } from './classic/MenuBar';
import { MainToolbar } from './classic/MainToolbar';
import { StatusBar } from './classic/StatusBar';
import { Navigator } from './classic/Navigator';
import { ClassicMarketWatch } from './classic/frames';
import { ClassicTrade, ExposurePanel, ToolboxWindow } from './classic/ToolboxPanels';
import { ClassicJournal } from './classic/ClassicJournal';
import {
  TOOLBOX,
  addClassicPanel,
  buildClassicLayout,
  isToolboxPanel,
  styleGroups,
  toggleToolbox,
  unstyleGroups,
} from './classic/classicLayout';
import { useOrderWindow } from './classic/orderWindowStore';

const components: Record<string, React.FunctionComponent<IDockviewPanelProps>> = {
  marketWatch: () => <MarketWatch />,
  chart: ChartPanel,
  trade: () => <TradePanel />,
  order: () => <OrderTicket />,
  history: () => <HistoryPanel />,
  notifications: () => <NotificationsPanel />,
  challenge: () => <ChallengePanel />,
  alerts: () => <AlertsPanel />,
  dataWindow: () => <DataWindow />,
  ticks: () => <TickChart />,
  mailbox: () => <MailboxPanel />,
  navigator: Navigator,
  exposure: () => <ExposurePanel />,
};

/** Wraps a Toolbox window in the strip with the Toolbox's close button. */
function inToolbox(Component: React.FunctionComponent<IDockviewPanelProps>) {
  const Framed = (props: IDockviewPanelProps) => <ToolboxWindow {...props} Component={Component} />;
  Framed.displayName = `Toolbox(${Component.displayName ?? Component.name})`;
  return Framed;
}

/** The classic workspace's windows: Market Watch with its title bar and views, the Toolbox windows in their strip. */
const classicComponents: Record<string, React.FunctionComponent<IDockviewPanelProps>> = {
  ...components,
  marketWatch: ClassicMarketWatch,
  trade: inToolbox(() => <ClassicTrade />),
  history: inToolbox(components.history!),
  notifications: inToolbox(() => <ClassicJournal />),
  challenge: inToolbox(components.challenge!),
  alerts: inToolbox(components.alerts!),
  mailbox: inToolbox(components.mailbox!),
  exposure: inToolbox(components.exposure!),
};

const MOBILE = { mobile: true };
const DESKTOP = { mobile: false };

/** Tells the user about a message from the broker that arrives while the terminal is open. */
function MailAnnouncer() {
  useMailAnnouncer();
  return null;
}

/** Checks the price alerts for as long as the workspace is open, whether or not the Alerts panel is. */
function AlertWatcher() {
  useAlertWatcher();
  return null;
}

/** Sounds and desktop notifications for what the log records. */
function NotificationEffects() {
  useNotificationEffects();
  return null;
}

/** Tells the workspace whether the account is a prop-firm challenge (inside the stream, which it reads). */
function ChallengeProbe({ targetRef }: { targetRef: React.RefObject<boolean> }) {
  const data = useChallenge().data;
  useEffect(() => {
    targetRef.current = data !== undefined && data !== null;
  }, [data, targetRef]);
  return null;
}

/** Keeps the browser tab title up to date (inside the stream, which it reads). */
function DocumentTitle() {
  useDocumentTitle();
  return null;
}

type Direction = 'left' | 'right' | 'above' | 'below' | 'within';

/**
 * The panels of the workspace, in Panels-menu order, and where each one goes when it is reopened: next to the first of
 * these panels that is still open (or wherever dockview puts it when none is).
 */
const PANELS: { id: string; title: string; near: [string, Direction][] }[] = [
  { id: 'marketWatch', title: 'Market Watch', near: [['chart', 'left']] },
  {
    id: 'chart',
    title: 'Chart',
    near: [
      ['marketWatch', 'right'],
      ['order', 'left'],
    ],
  },
  {
    id: 'trade',
    title: 'Trade',
    near: [
      ['history', 'within'],
      ['chart', 'below'],
    ],
  },
  {
    id: 'history',
    title: 'History',
    near: [
      ['trade', 'within'],
      ['chart', 'below'],
    ],
  },
  { id: 'order', title: 'New Order', near: [['chart', 'right']] },
  {
    id: 'notifications',
    title: 'Notifications',
    near: [
      ['history', 'within'],
      ['trade', 'within'],
    ],
  },
  {
    id: 'challenge',
    title: 'Challenge',
    near: [
      ['trade', 'within'],
      ['history', 'within'],
    ],
  },
  {
    id: 'mailbox',
    title: 'Mailbox',
    near: [
      ['notifications', 'within'],
      ['history', 'within'],
      ['trade', 'within'],
    ],
  },
  {
    id: 'ticks',
    title: 'Tick chart',
    near: [
      ['chart', 'within'],
      ['marketWatch', 'below'],
    ],
  },
  {
    id: 'dataWindow',
    title: 'Data Window',
    near: [
      ['marketWatch', 'within'],
      ['chart', 'left'],
    ],
  },
  {
    id: 'alerts',
    title: 'Alerts',
    near: [
      ['history', 'within'],
      ['trade', 'within'],
    ],
  },
  { id: 'navigator', title: 'Navigator', near: [['marketWatch', 'below']] },
  {
    id: 'exposure',
    title: 'Exposure',
    near: [
      ['trade', 'within'],
      ['history', 'within'],
    ],
  },
];

/** The titles the classic workspace gives its windows. */
const CLASSIC_TITLES: Record<string, string> = Object.fromEntries(TOOLBOX.map((t) => [t.id, t.title]));

/** Shows a workspace panel, opening it again where it belongs if the user closed it. */
function showPanel(api: DockviewApi, id: string, classic = false): void {
  const existing = api.getPanel(id);
  if (existing) return existing.api.setActive();
  const spec = PANELS.find((p) => p.id === id);
  if (!spec) return;
  if (classic && addClassicPanel(api, id, CLASSIC_TITLES[id] ?? spec.title)) {
    styleGroups(api);
    return;
  }
  const near = spec.near.find(([ref]) => api.getPanel(ref));
  api.addPanel({
    id,
    component: id,
    title: spec.title,
    position: near ? { referencePanel: near[0], direction: near[1] } : undefined,
  });
}

function buildDefaultLayout(api: DockviewApi): void {
  api.clear();
  const marketWatch = api.addPanel({ id: 'marketWatch', component: 'marketWatch', title: 'Market Watch' });
  const chart = api.addPanel({
    id: 'chart',
    component: 'chart',
    title: 'Chart',
    position: { referencePanel: marketWatch, direction: 'right' },
  });
  const trade = api.addPanel({
    id: 'trade',
    component: 'trade',
    title: 'Trade',
    position: { referencePanel: chart, direction: 'below' },
  });
  api.addPanel({
    id: 'history',
    component: 'history',
    title: 'History',
    position: { referencePanel: trade, direction: 'within' },
  });
  api.addPanel({
    id: 'order',
    component: 'order',
    title: 'New Order',
    position: { referencePanel: chart, direction: 'right' },
  });
  marketWatch.api.setSize({ width: 380 });
  trade.api.setSize({ height: 220 });
  trade.api.setActive(); // History was added last and would otherwise be the visible tab
  chart.api.setActive();
}

/** The dockable workspace. The layout is part of the user's synced preferences, so it follows them to every device. */
export function Workspace() {
  const apiRef = useRef<DockviewApi | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [profilesOpen, setProfilesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // The first-run guide opens by itself once (until seen or skipped), and again from the menu.
  const guideDue = useGuideDue();
  const [guideAsked, setGuideAsked] = useState(false);
  const [guideClosed, setGuideClosed] = useState(false);
  const openGuide = () => {
    setGuideClosed(false);
    setGuideAsked(true);
  };
  const userId = useSession().user?.id;
  const narrow = useIsNarrow();
  const shell = usePrefsValue((s) => shellOf(s.data));
  const classic = shell === 'classic' && !narrow;
  const classicRef = useRef(classic);
  const challengeRef = useRef(false);
  useEffect(() => {
    classicRef.current = classic;
  });
  const [profileName, setProfileName] = useState('Default');
  // Re-renders the toolbar's window buttons when windows open and close.
  const [, setLayoutVersion] = useState(0);

  // The log survives a reload of this tab, for this user; signing out empties it.
  useEffect(() => {
    if (!userId) return;
    const stop = persistNotifications(notifications, userId);
    return () => {
      stop();
      notifications.getState().clear();
    };
  }, [userId]);
  const ready = usePrefsSync();

  const show = useCallback((id: string) => {
    if (apiRef.current) showPanel(apiRef.current, id, classicRef.current);
  }, []);

  /** The default layout of the workspace in use. */
  const buildDefault = useCallback((api: DockviewApi) => {
    if (classicRef.current) buildClassicLayout(api, challengeRef.current);
    else {
      unstyleGroups(api);
      buildDefaultLayout(api);
    }
    prefsStore.set('layoutShell', classicRef.current ? 'classic' : 'modern');
  }, []);

  const applyProfile = useCallback((name: string) => {
    const api = apiRef.current;
    const profile = normalizeProfiles(prefsStore.getState().data.profiles)[name];
    if (!api || !profile) return;
    const before = api.toJSON();
    try {
      api.fromJSON(profile.layout as Parameters<DockviewApi['fromJSON']>[0]);
      if (classicRef.current) styleGroups(api);
      prefsStore.set('layout', api.toJSON());
      setProfileName(name);
    } catch {
      api.fromJSON(before); // a layout from an older version: keep the one in use
    }
  }, []);
  /**
   * Shows the order ticket, like F9 in MT5: the classic shell's own rich Order window (blank, for a new trade), or
   * the docked New Order panel otherwise.
   */
  const showOrderTicket = useCallback(() => {
    if (classicRef.current) return useOrderWindow.getState().open({ kind: 'new' });
    show('order');
  }, [show]);

  /**
   * Opens another chart next to the first one. It starts on the given symbol, or whatever Market Watch is on right now,
   * but — unlike the very first chart — does not keep following Market Watch afterwards: picking a different symbol
   * elsewhere only moves the chart you're looking at, not every open chart tab.
   */
  const newChart = useCallback((symbol?: string) => {
    const api = apiRef.current;
    if (!api) return;
    const n = api.panels.filter((p) => p.id.startsWith('chart')).length;
    const start = symbol ?? useWorkspace.getState().activeSymbol ?? undefined;
    api.addPanel({
      id: `chart-${Date.now().toString(36)}`,
      component: 'chart',
      title: symbol ?? `Chart ${n + 1}`, // the chart retitles itself with its symbol and timeframe
      params: { ...(start ? { symbol: start } : {}), linked: false },
      position: api.getPanel('chart') ? { referencePanel: 'chart', direction: 'within' } : undefined,
    });
  }, []);

  // Other panels (Market Watch's menu, say) open the ticket or a chart through the workspace store.
  useEffect(() => {
    if (narrow) return; // the mobile layout registers its own
    useWorkspace.getState().setActions({
      orderTicket: showOrderTicket,
      newChart,
      showPanel: show,
      applyProfile,
      openSettings: () => setSettingsOpen(true),
    });
    return () => useWorkspace.getState().setActions(null);
  }, [showOrderTicket, newChart, narrow, show, applyProfile]);

  /** What the Panels menu lists: every workspace panel with whether it is open, then the extra charts. */
  const listPanels = useCallback((): PanelEntry[] => {
    const api = apiRef.current;
    if (!api) return [];
    const fixed = PANELS.map((p) => ({
      id: p.id,
      title: p.title,
      open: api.getPanel(p.id) !== undefined,
      extra: false,
    }));
    const extras = api.panels
      .filter((p) => p.api.component === 'chart' && p.id !== 'chart')
      .map((p) => ({ id: p.id, title: p.title ?? 'Chart', open: true, extra: true }));
    return [...fixed, ...extras];
  }, []);

  const togglePanel = useCallback(
    (id: string) => {
      const existing = apiRef.current?.getPanel(id);
      if (existing) existing.api.close();
      else show(id);
    },
    [show],
  );
  const toggleTools = useCallback(() => {
    if (apiRef.current) toggleToolbox(apiRef.current, challengeRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F9' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        useWorkspace.getState().actions?.orderTicket(); // the dock's ticket, or the phone's sheet
      }
      // Ctrl+D shows or hides the Data Window, as in MT5
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        togglePanel('dataWindow');
      }
      if (classicRef.current && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
        // Ctrl+M Market Watch; Ctrl+N Navigator and Ctrl+T Toolbox where the browser lets the page have them (installed app)
        const key = e.key.toLowerCase();
        if (key === 'm' || key === 'n' || key === 't') {
          e.preventDefault();
          if (key === 't') toggleTools();
          else togglePanel(key === 'm' ? 'marketWatch' : 'navigator');
        }
      }
      if (classicRef.current && e.key === 'F1' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
      // "?" lists the shortcuts, unless the user is typing it into a field
      const target = e.target as HTMLElement | null;
      const typing = target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable);
      if (e.key === '?' && !typing && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showOrderTicket, togglePanel, toggleTools]);

  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      const api = event.api;
      apiRef.current = api;
      let restored = false;
      const data = prefsStore.getState().data;
      const saved = data.layout;
      // A layout saved for the other workspace is not restored (older layouts, with no mark, are the Adiox one).
      const madeFor = data.layoutShell ?? 'modern';
      if (saved && madeFor === (classicRef.current ? 'classic' : 'modern')) {
        try {
          api.fromJSON(saved as Parameters<DockviewApi['fromJSON']>[0]);
          restored = true;
        } catch {
          restored = false; // a layout from an older version: fall back to the default
        }
      }
      if (!restored) {
        buildDefault(api);
      } else if (classicRef.current) {
        styleGroups(api);
      }
      let timer: ReturnType<typeof setTimeout> | undefined;
      api.onDidLayoutChange(() => {
        if (classicRef.current) styleGroups(api);
        setLayoutVersion((v) => v + 1);
        clearTimeout(timer);
        timer = setTimeout(() => prefsStore.set('layout', api.toJSON()), 500);
      });
    },
    [buildDefault],
  );

  const classicActions: ClassicActions = {
    newChart: () => newChart(),
    newOrder: showOrderTicket,
    listPanels,
    togglePanel,
    toggleToolbox: toggleTools,
    toolboxOpen: () => apiRef.current?.panels.some((p) => isToolboxPanel(p.api.component)) ?? false,
    listCharts: () =>
      (apiRef.current?.panels ?? [])
        .filter((p) => p.api.component === 'chart')
        .map((p) => ({ id: p.id, title: p.title ?? 'Chart', active: p.api.isActive })),
    activateChart: (id) => apiRef.current?.getPanel(id)?.api.setActive(),
    openProfiles: () => setProfilesOpen(true),
    openSettings: () => setSettingsOpen(true),
    openShortcuts: () => setShortcutsOpen(true),
    openGuide,
    resetLayout: () => setConfirmReset(true),
  };

  if (!ready) {
    return (
      <main
        className="grid h-full place-items-center bg-bg"
        aria-busy="true"
        aria-label="Loading your workspace"
      >
        <Wordmark className="h-10 animate-pulse" />
      </main>
    );
  }

  return (
    <StreamProvider>
      <LayoutContext.Provider value={narrow ? MOBILE : DESKTOP}>
        {narrow ? (
          <MobileWorkspace onOpenSettings={() => setSettingsOpen(true)} onOpenGuide={openGuide} />
        ) : classic ? (
          <div className="classic-shell flex h-full flex-col bg-bg" data-shell="classic">
            <MenuBar actions={classicActions} />
            <MainToolbar actions={classicActions} />
            <main className="min-h-0 flex-1">
              <DockviewReact
                className="adiox-dock classic-dock"
                components={classicComponents}
                defaultTabComponent={DockTab}
                onReady={onReady}
              />
            </main>
            <StatusBar profile={profileName} />
          </div>
        ) : (
          <div className="flex h-full flex-col bg-bg">
            <TopBar
              onNewOrder={showOrderTicket}
              onShowNotifications={() => show('notifications')}
              onShowMailbox={() => show('mailbox')}
              listPanels={listPanels}
              onTogglePanel={togglePanel}
              onOpenSettings={() => setSettingsOpen(true)}
              onOpenShortcuts={() => setShortcutsOpen(true)}
              onOpenGuide={openGuide}
              onNewChart={() => newChart()}
              onResetLayout={() => setConfirmReset(true)}
              onOpenProfiles={() => setProfilesOpen(true)}
            />
            <main className="min-h-0 flex-1 p-2">
              <DockviewReact
                className="adiox-dock"
                components={components}
                defaultTabComponent={DockTab}
                onReady={onReady}
              />
            </main>
          </div>
        )}
        <ReadOnlyBanner />
        <PaymentsDialog />
        <NewPasswordRequired />
        <AlertWatcher />
        <MailAnnouncer />
        <NotificationEffects />
        <DocumentTitle />
        <ChallengeProbe targetRef={challengeRef} />
        <NotificationFeed />
        <Toasts />
        <SettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onOpenShortcuts={() => {
            setSettingsOpen(false);
            setShortcutsOpen(true);
          }}
        />
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <WelcomeGuide
          open={!guideClosed && (guideAsked || guideDue)}
          onClose={() => {
            setGuideClosed(true);
            setGuideAsked(false);
          }}
        />
        <ProfilesDialog
          open={profilesOpen}
          onClose={() => setProfilesOpen(false)}
          current={() => apiRef.current?.toJSON() ?? null}
          apply={(layout) => {
            const api = apiRef.current;
            if (!api) return false;
            const before = api.toJSON();
            try {
              api.fromJSON(layout as Parameters<DockviewApi['fromJSON']>[0]);
              if (classicRef.current) styleGroups(api);
              prefsStore.set('layout', api.toJSON());
              return true;
            } catch {
              api.fromJSON(before); // a layout from an older version: keep the one in use
              return false;
            }
          }}
        />
        <ConfirmDialog
          open={confirmReset}
          title="Reset the layout?"
          confirmLabel="Reset layout"
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => {
            setConfirmReset(false);
            if (apiRef.current) buildDefault(apiRef.current);
            setProfileName('Default');
          }}
        >
          <p>
            Every panel goes back to where it started. Extra charts are closed, and the indicators and
            settings of every chart are removed. Drawings and chart templates are kept. This cannot be undone.
          </p>
        </ConfirmDialog>
      </LayoutContext.Provider>
    </StreamProvider>
  );
}
